import { useEffect, useRef } from 'react'
import { GS1Code128Element } from '@/types'
import { useSettingsStore } from '@/stores/settingsStore'
import { useLabelStore } from '@/stores/labelStore'
import { encodeGS1ForBwipJs } from '@/lib/gs1/gs1Encoder'
import bwipjs from 'bwip-js'

interface GS1Code128ElementPreviewProps {
  element: GS1Code128Element
  zoom: number
  mmToPx: number
}

export function GS1Code128ElementPreview({ element, zoom, mmToPx }: GS1Code128ElementPreviewProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const { variableData } = useSettingsStore()
  const { label } = useLabelStore()

  // Encode GS1 data using same format as GS1 DataMatrix (^FNC1 markers)
  const gs1Data = encodeGS1ForBwipJs(element.applicationIdentifiers, variableData)

  // Calculate barcode dimensions to match ZPL output
  // In ZPL: moduleWidth is in dots, height is in mm
  const dotsPerMm = label.dpi / 25.4
  const heightInPx = element.height * mmToPx * zoom

  // For bwip-js:
  // - scale: pixels per module (narrow bar width in pixels)
  // - height: barcode height in millimeters
  // Convert moduleWidth from dots to mm, then to screen pixels
  const moduleWidthMm = element.moduleWidth / dotsPerMm
  const scale = moduleWidthMm * mmToPx * zoom

  // bwip-js height is in mm when using scale
  const heightMm = element.height

  useEffect(() => {
    if (!canvasRef.current) return

    const ctx = canvasRef.current.getContext('2d', { willReadFrequently: true })
    if (!ctx) return

    // If no AIs defined, show placeholder
    if (!gs1Data || element.applicationIdentifiers.length === 0) {
      const placeholderWidth = 100 * zoom
      canvasRef.current.width = placeholderWidth
      canvasRef.current.height = heightInPx
      ctx.fillStyle = '#f0f0f0'
      ctx.fillRect(0, 0, placeholderWidth, heightInPx)
      ctx.strokeStyle = '#ccc'
      ctx.strokeRect(0, 0, placeholderWidth, heightInPx)
      ctx.fillStyle = '#666'
      ctx.font = `${10 * zoom}px Arial`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText('GS1-128', placeholderWidth / 2, heightInPx / 2)
      return
    }

    try {
      // Use code128 with parsefnc to handle ^FNC1 markers
      // Data format: ^FNC10107891234567890172512311^FNC1ABC123
      bwipjs.toCanvas(canvasRef.current, {
        bcid: 'code128',
        text: gs1Data,
        scale: scale,
        height: heightMm,
        includetext: element.showText,
        textxalign: 'center',
        parsefnc: true, // Parse ^FNC1 as FNC1 character
      })
    } catch (e) {
      console.error('GS1-128 error:', e, 'Data:', gs1Data)
      // Invalid GS1 data - show error placeholder
      const errorWidth = 120 * zoom
      canvasRef.current.width = errorWidth
      canvasRef.current.height = heightInPx
      ctx.fillStyle = '#fee2e2'
      ctx.fillRect(0, 0, errorWidth, heightInPx)
      ctx.strokeStyle = '#ef4444'
      ctx.lineWidth = 2
      ctx.strokeRect(1, 1, errorWidth - 2, heightInPx - 2)
      ctx.fillStyle = '#dc2626'
      ctx.font = `${Math.max(9, 9 * zoom)}px Arial`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText('GS1-128', errorWidth / 2, heightInPx / 2)
    }
  }, [gs1Data, scale, heightInPx, heightMm, zoom, element.applicationIdentifiers.length, element.showText])

  return (
    <div style={{ minHeight: heightInPx }}>
      <canvas
        ref={canvasRef}
        style={{ display: 'block' }}
      />
    </div>
  )
}
